import { useEffect, useRef, useState } from 'react';
import { ACCEPT_CV, FICHIERS_CV_PAR_SELECTION_MAX, type CandidatProbable, type ReponseDepotCv } from '@rh/shared';
import {
  LIBELLES_ETAT_ENVOI_CV,
  envoyerCv,
  formaterTaille,
  messageEnvoiCv,
  messageResultatDepot,
  preparerEnvois,
  type EnvoiCv,
} from '../envoi-cv';
import { nomComplet } from '../format';
import { ChoixCandidatureCv, type CandidatureProposee } from './ChoixCandidatureCv';

interface Props {
  /** Chaque réponse : la page insère le dépôt dans sa liste sans la relire. */
  onReponse: (reponse: ReponseDepotCv) => void;
  /** Série terminée avec au moins un dépôt abouti : la page relit sa liste, une fois. */
  onFileVide: () => void;
}

function maj(liste: EnvoiCv[], cle: string, champs: Partial<EnvoiCv>): EnvoiCv[] {
  return liste.map((envoi) => (envoi.cle === cle ? { ...envoi, ...champs } : envoi));
}

/**
 * Dépôt de plusieurs CV (PC et téléphone, iOS compris) : choix multiple ou
 * glisser-déposer, puis envoi d'un fichier par requête, l'un après l'autre,
 * avec l'état et l'erreur de chaque fichier. Aucun `capture` : le sélecteur
 * du téléphone garde l'accès aux Téléchargements et à l'app Fichiers.
 */
export function ZoneDepotCv({ onReponse, onFileVide }: Props) {
  const [envois, setEnvois] = useState<EnvoiCv[]>([]);
  const [ignores, setIgnores] = useState(0);
  const [survol, setSurvol] = useState(false);
  const [copie, setCopie] = useState<string | null>(null);
  const controleur = useRef<AbortController | null>(null);
  const vivant = useRef(true);
  const aboutis = useRef(0);
  const rappels = useRef({ onReponse, onFileVide });

  useEffect(() => {
    rappels.current = { onReponse, onFileVide };
  }, [onReponse, onFileVide]);

  // Démontage : l'envoi en cours est abandonné, aucune réponse tardive n'est appliquée.
  useEffect(() => {
    const envoiEnCours = controleur;
    vivant.current = true;
    return () => {
      vivant.current = false;
      envoiEnCours.current?.abort();
    };
  }, []);

  // Un fichier lâché à côté de la zone serait ouvert par le navigateur à la place
  // de l'application, et la file d'envoi perdue : seul un glissement de fichiers est retenu.
  useEffect(() => {
    const retenir = (evenement: DragEvent) => {
      if (Array.from(evenement.dataTransfer?.types ?? []).includes('Files')) evenement.preventDefault();
    };
    window.addEventListener('dragover', retenir);
    window.addEventListener('drop', retenir);
    return () => {
      window.removeEventListener('dragover', retenir);
      window.removeEventListener('drop', retenir);
    };
  }, []);

  const enCours = envois.some((e) => e.etat === 'envoi');
  const prochain = envois.find((e) => e.etat === 'en_file') ?? null;

  // Un seul envoi à la fois : le suivant part quand le précédent a sa réponse.
  useEffect(() => {
    if (enCours) return;
    if (!prochain) {
      if (aboutis.current > 0) {
        aboutis.current = 0;
        rappels.current.onFileVide();
      }
      return;
    }
    const { cle, fichier } = prochain;
    const c = new AbortController();
    controleur.current = c;
    setEnvois((liste) => maj(liste, cle, { etat: 'envoi', message: null, reessayable: false }));
    envoyerCv('/cv-a-rattacher', fichier, c.signal).then(
      (reponse) => {
        if (!vivant.current) return;
        aboutis.current += 1;
        setEnvois((liste) => maj(liste, cle, { etat: 'termine', message: messageResultatDepot(reponse), reponse }));
        rappels.current.onReponse(reponse);
      },
      (erreur: unknown) => {
        if (!vivant.current) return;
        const classe = messageEnvoiCv(erreur) ?? { etat: 'erreur_reseau' as const, message: null, reessayable: true };
        setEnvois((liste) => maj(liste, cle, classe));
      },
    );
  }, [enCours, prochain]);

  function ajouter(fichiers: FileList | null | undefined): void {
    if (!fichiers || fichiers.length === 0) return;
    const selection = preparerEnvois(fichiers);
    setIgnores(selection.ignores);
    setEnvois((liste) => [...liste, ...selection.envois]);
  }

  async function deposerCopie(envoi: EnvoiCv, candidat: CandidatProbable, cible: CandidatureProposee): Promise<void> {
    const destination = `${nomComplet(candidat)} — ${cible.annonceIntitule}`;
    if (!window.confirm(`Déposer une copie de ce CV sur la fiche de ${destination} ?`)) return;
    setCopie(envoi.cle);
    try {
      const reponse = await envoyerCv(`/candidatures/${cible.candidatureId}/documents`, envoi.fichier);
      if (!vivant.current) return;
      setEnvois((liste) =>
        maj(liste, envoi.cle, {
          message:
            reponse.resultat === 'deja_present'
              ? messageResultatDepot(reponse)
              : `Copie déposée sur la fiche de ${destination}`,
          reponse: envoi.reponse ? { ...envoi.reponse, copiesPossibles: [] } : null,
        }),
      );
    } catch (erreur) {
      const classe = messageEnvoiCv(erreur);
      if (classe && vivant.current) {
        setEnvois((liste) => maj(liste, envoi.cle, { message: `Copie non déposée : ${classe.message}` }));
      }
    } finally {
      if (vivant.current) setCopie(null);
    }
  }

  const total = envois.length;
  const finis = envois.filter((e) => e.etat !== 'en_file' && e.etat !== 'envoi').length;

  return (
    <div className="depot-cv">
      <div
        className={`zone-depot-cv${survol ? ' zone-depot-cv--survol' : ''}`}
        data-testid="zone-depot-cv"
        onDragEnter={(e) => {
          e.preventDefault();
          setSurvol(true);
        }}
        onDragOver={(e) => e.preventDefault()}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setSurvol(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          setSurvol(false);
          ajouter(e.dataTransfer.files);
        }}
      >
        <label className="bouton bouton-fichier">
          Choisir des CV
          <input
            type="file"
            multiple
            accept={ACCEPT_CV}
            onChange={(e) => {
              ajouter(e.target.files);
              e.target.value = '';
            }}
          />
        </label>
        <p className="glisser-cv">ou glissez les fichiers ici</p>
        <p className="mention">
          PDF, Word (.docx ou .doc), JPEG ou PNG · 10 Mo au plus · {FICHIERS_CV_PAR_SELECTION_MAX} fichiers par
          sélection
        </p>
      </div>

      {ignores > 0 && (
        <p className="signal">
          {ignores} fichier(s) non pris : {FICHIERS_CV_PAR_SELECTION_MAX} au plus par sélection, choisissez les
          suivants ensuite.
        </p>
      )}

      {total > 0 && (
        <div className="progression-cv">
          <progress max={total} value={finis} aria-label="Fichiers traités" />
          <span aria-live="polite">
            {finis < total ? `${finis} sur ${total} traité(s) · envoi un par un` : `${total} fichier(s) traité(s)`}
          </span>
          {finis === total && (
            <button type="button" className="lien" onClick={() => setEnvois([])}>
              Vider la liste
            </button>
          )}
        </div>
      )}

      {total > 0 && (
        <ul className="envois-cv">
          {envois.map((envoi) => (
            <li key={envoi.cle} className={`envoi-cv envoi-cv--${envoi.etat}`}>
              <div className="ligne-envoi">
                <span className="nom-fichier">{envoi.fichier.name}</span>
                <span className="mention">{formaterTaille(envoi.fichier.size)}</span>
                <span className="etat-envoi-cv">{LIBELLES_ETAT_ENVOI_CV[envoi.etat]}</span>
              </div>
              {envoi.message && (
                <p className={envoi.etat === 'refuse' || envoi.etat === 'erreur_reseau' ? 'alerte' : 'mention'}>
                  {envoi.message}
                </p>
              )}
              {envoi.reponse?.resultat === 'deja_rattache' && envoi.reponse.copiesPossibles.length > 0 && (
                <div className="copies-cv">
                  <p className="mention">
                    La même personne a une autre candidature sans CV ? Déposez-y une copie :
                  </p>
                  <ChoixCandidatureCv
                    candidats={envoi.reponse.copiesPossibles}
                    libelleAction="Déposer une copie ici"
                    envoi={copie !== null}
                    onChoisir={(candidat, cible) => void deposerCopie(envoi, candidat, cible)}
                  />
                </div>
              )}
              {(envoi.etat === 'en_file' || envoi.etat === 'refuse' || envoi.etat === 'erreur_reseau') && (
                <div className="actions-envoi">
                  {envoi.reessayable && (
                    <button
                      type="button"
                      className="bouton-second"
                      aria-label={`Réessayer ${envoi.fichier.name}`}
                      onClick={() =>
                        setEnvois((liste) => maj(liste, envoi.cle, { etat: 'en_file', message: null }))
                      }
                    >
                      Réessayer
                    </button>
                  )}
                  <button
                    type="button"
                    className="lien"
                    aria-label={`Retirer ${envoi.fichier.name} de la liste`}
                    onClick={() => setEnvois((liste) => liste.filter((e) => e.cle !== envoi.cle))}
                  >
                    {envoi.etat === 'en_file' ? 'Retirer de la file' : 'Retirer de la liste'}
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
